import React from "react";
import {
  MapPin, TrendingUp, Wallet, CalendarCheck, ArrowRight, Star,
  ShieldCheck, Clock, Sparkles, CheckCircle2, Phone,
} from "lucide-react";
import { TalentRegisterForm } from "./TalentRegister";

const HERO_IMG =
  "https://images.unsplash.com/photo-1647381518264-97ff1835026f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const BENEFITS = [
  { icon: MapPin, color: "from-blue-500 to-blue-600", title: "Perto de casa", text: "Receba chamados de serviços na sua região — menos deslocamento, mais tempo produtivo." },
  { icon: TrendingUp, color: "from-emerald-500 to-emerald-600", title: "Mais oportunidades", text: "Várias empresas encontram você pelo mapa. Mais serviços, mais renda no fim do mês." },
  { icon: Wallet, color: "from-violet-500 to-violet-600", title: "Pagamento em dia", text: "Valores combinados por diária e pagamentos organizados por Zelle/Venmo." },
  { icon: CalendarCheck, color: "from-amber-500 to-amber-600", title: "Você no controle", text: "Informe os dias que pode trabalhar. Você decide sua disponibilidade." },
];

const STEPS = [
  { n: 1, title: "Cadastre-se em 2 minutos", text: "Preencha seus dados, região, foto e os dias que pode trabalhar. É grátis." },
  { n: 2, title: "Apareça no mapa", text: "Seu perfil entra no Banco de Colaboradores e fica visível para empresas da sua área." },
  { n: 3, title: "Receba chamados perto de você", text: "Quando surge um serviço na sua região, a empresa chama o reforço mais próximo." },
];

export default function CollaboratorLanding() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="collab-landing">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="collab-landing-home">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white shadow-sm shadow-blue-600/30">
              <Sparkles size={18} />
            </span>
            <span className="font-extrabold text-lg tracking-tight">Clean <span className="text-blue-600">Manager</span></span>
          </a>
          <a
            href="#cadastro"
            data-testid="collab-landing-nav-cta"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
          >
            Cadastre-se <ArrowRight size={16} />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-white" />
        <div className="relative max-w-6xl mx-auto px-4 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wide">
              <MapPin size={13} /> Para profissionais de limpeza
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Trabalhe perto de casa e <span className="text-blue-600">receba mais chamados</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl">
              Cadastre-se no Banco de Colaboradores do Clean Manager e apareça no mapa para empresas
              de limpeza da sua região. Mais oportunidades, menos deslocamento e pagamento em dia.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#cadastro"
                data-testid="collab-landing-hero-cta"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold shadow-lg shadow-blue-600/25 transition-colors"
              >
                Quero me cadastrar <ArrowRight size={18} />
              </a>
              <a
                href="#como-funciona"
                data-testid="collab-landing-how-link"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-slate-200 hover:border-blue-300 text-slate-700 text-base font-semibold transition-colors"
              >
                Como funciona
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-emerald-500" /> Cadastro grátis</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={16} className="text-blue-500" /> Leva 2 minutos</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} className="text-violet-500" /> Seus dados protegidos</span>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/5">
              <img src={HERO_IMG} alt="Profissional de limpeza" className="w-full h-[300px] sm:h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-2 sm:left-4 bg-white rounded-2xl shadow-xl p-4 w-56 border border-slate-100" data-testid="collab-landing-float-card">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <MapPin size={14} className="text-blue-600" /> Chamados perto de você
              </div>
              <div className="mt-1 text-2xl font-extrabold text-slate-900">8 <span className="text-sm font-semibold text-slate-400">na sua região</span></div>
              <div className="mt-2 flex items-center gap-1 text-amber-500">
                {["s1", "s2", "s3", "s4", "s5"].map((k) => <Star key={k} size={14} fill="currentColor" />)}
                <span className="text-xs text-slate-500 ml-1">avaliação 4.9</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
        <div className="max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Por que se cadastrar?</h2>
          <p className="mt-2 text-slate-600">Feito para quem quer trabalhar mais perto, com mais previsibilidade.</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b) => (
            <div key={b.title} data-testid={`collab-benefit-${b.title}`}
              className="rounded-2xl border border-slate-100 p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all bg-white">
              <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${b.color} flex items-center justify-center text-white`}>
                <b.icon size={20} />
              </span>
              <h3 className="mt-4 font-bold text-lg">{b.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Como funciona</h2>
            <p className="mt-2 text-slate-600">Do cadastro ao primeiro chamado em 3 passos simples.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} data-testid={`collab-step-${s.n}`} className="relative bg-white rounded-2xl border border-slate-100 p-6">
                <span className="w-11 h-11 rounded-full bg-blue-600 text-white font-extrabold text-lg flex items-center justify-center">{s.n}</span>
                <h3 className="mt-4 font-bold text-lg">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-3 gap-4 text-center">
          {[["150+", "empresas na rede"], ["Grátis", "sempre para o colaborador"], ["Perto", "de onde você mora"]].map(([v, l]) => (
            <div key={l} className="rounded-2xl bg-blue-50 p-5">
              <div className="text-2xl sm:text-3xl font-extrabold text-blue-700">{v}</div>
              <div className="text-xs sm:text-sm text-slate-600 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CADASTRO (formulário embutido) */}
      <section id="cadastro" className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-14 sm:py-20">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Faça seu cadastro</h2>
            <p className="mt-2 text-slate-600">Grátis e leva 2 minutos. Preencha e nossa rede encontra você.</p>
          </div>
          <TalentRegisterForm />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10" />
          <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-white/10" />
          <div className="relative">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">Pronto para começar?</h2>
            <p className="mt-3 text-blue-100 max-w-xl mx-auto">
              Faça seu cadastro gratuito e comece a receber chamados de serviços perto de você.
            </p>
            <a
              href="#cadastro"
              data-testid="collab-landing-final-cta"
              className="mt-7 inline-flex items-center gap-2 px-8 h-13 py-4 rounded-full bg-white text-blue-700 font-bold hover:bg-blue-50 transition-colors"
            >
              Cadastrar agora — é grátis <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
          <span className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white"><Sparkles size={14} /></span>
            Clean Manager USA · {new Date().getFullYear()}
          </span>
          <div className="flex items-center gap-4">
            <a href="/" className="hover:text-blue-600">Voltar ao site</a>
            <a href="#cadastro" className="hover:text-blue-600 font-semibold">Cadastre-se</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
